import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import styles from "./DynamicSlider.module.css";
import { Link } from "react-router-dom";
import { getImageUrl, getFallbackImageUrl } from "../../../utils/imageUtils";

export const DynamicSlider = ({ section, subSection }) => {
  const [bannerData, setBannerData] = useState(null);
  const [fallbackData, setFallbackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBannerData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/banners/section/${section}/subsection/${subSection}`);

        if (response.status === 404) {
          // Si no se encontraron banners, usar el fallback
          setBannerData(null);
          setError(null);
          return;
        }

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setBannerData(data[0]); // Asumiendo que la respuesta es un array y tomamos el primer elemento
        setError(null);
      } catch (err) {
        console.error("Error fetching banner data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchFallbackData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/error-page`);
        if (response.ok) {
          const data = await response.json();
          setFallbackData(data[0]); // Tomamos el primer elemento del array
        }
      } catch (err) {
        console.error("Error fetching fallback data:", err);
      }
    };

    if (section && subSection) {
      fetchBannerData();
      fetchFallbackData(); // Siempre cargar los datos de fallback
    }
  }, [section, subSection]);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p className={styles.loadingText}>Cargando banners...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>⚠️</div>
        <p className={styles.errorText}>Error al cargar los banners: {error}</p>
      </div>
    );
  }

  // Si no hay bannerData o no tiene carousel válido, mostrar el fallback
  if (!bannerData || !bannerData.carousel || !bannerData.carousel.items || bannerData.carousel.items.length === 0) {
    if (fallbackData) {
      const fullFallbackImageUrl = getFallbackImageUrl(fallbackData);
      const originalUrl = fallbackData.originalUrl || '';
      const extension = originalUrl.split('.').pop().toLowerCase();
      const isVideo = ["mp4", "webm", "ogg"].includes(extension);
      const isImage = ["jpg", "jpeg", "png", "webp", "gif", "bmp", "svg"].includes(extension);
      const mediaUrl = originalUrl.startsWith('http') ? originalUrl : `${process.env.REACT_APP_API_URL}${originalUrl}`;

      return (
        <div className={styles.fallbackContainer}>
          <div className={styles.fallbackImageContainer}>
            {isVideo ? (
              <video
                src={mediaUrl}
                autoPlay
                loop
                muted
                playsInline
                className={styles.fallbackImage}
                style={{background: '#000'}}
              />
            ) : isImage ? (
              <img
                src={mediaUrl}
                alt="Banner de respaldo"
                className={styles.fallbackImage}
              />
            ) : null}
            <div className={styles.fallbackOverlay}>
              <div className={styles.fallbackContent}>
                {fallbackData.url && (
                  <Link
                    to={fallbackData.url}
                    onClick={handleScrollToTop}
                    className={styles.fallbackButton}
                  >
                    {fallbackData.message || "Visitar página"}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className={styles.updatingContainer}>
        <div className={styles.updatingIcon}>🔄</div>
        <p className={styles.updatingText}>Estamos actualizando esta sección</p>
      </div>
    );
  }

  const { carousel } = bannerData;
  const hasMultipleItems = carousel.items.length > 1;
  // console.log( "carousel", carousel);

  // Configuración del slider basada en los datos de la API
  const settings = {
    pauseOnHover: false,
    dots: false,
    infinite: hasMultipleItems,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: carousel.autoplay && hasMultipleItems,
    autoplaySpeed: carousel.speed,
    arrows: hasMultipleItems,
    fade: true,
    adaptiveHeight: false,
    accessibility: false,
  };

  // Función para renderizar un item individual
  const renderItem = (item, index) => {
    const imageUrl = getImageUrl(item, 'large');
    const imageTitle = item.imageTitle || bannerData.imageTitle || `Banner ${index + 1}`;
    const itemKey = item._id || item.imageUrl || index;

    return (
      <div className={styles.contenedorImg} key={itemKey}>
        <img
          src={imageUrl}
          alt={imageTitle}
          className={styles.sliderImage}
        />
        {item.button?.enabled && (
          <Link
            to={item.button.url}
            onClick={handleScrollToTop}
            className={styles.button}
            style={{
              position: 'absolute',
              left: `${item.button.position?.x || 50}%`,
              top: `${item.button.position?.y || 50}%`,
              transform: 'translate(-50%, -50%)',
              color: item.button.textColor || '#FFFFFF',
              backgroundColor: item.button.backgroundColor || '#89182F',
              borderRadius: "20px",
              padding: '10px 20px',
              textDecoration: 'none',
              fontSize: '16px',
              fontWeight: 'bold',
            }}
          >
            {item.button.text}
          </Link>
        )}
      </div>
    );
  };

  return (
    <div className={styles.sliderContainer}>
      {hasMultipleItems ? (
        <Slider {...settings}>
          {carousel.items.map((item, index) => renderItem(item, index))}
        </Slider>
      ) : (
        renderItem(carousel.items[0], 0)
      )}
    </div>
  );
}; 